import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import { create } from '@bufbuild/protobuf'
import {
  listFailedWebhooks,
  listStaleWebhooks,
  replayWebhook,
  replayFailedWebhooks,
  replayStaleWebhooks,
  adminListOrganizations,
} from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListFailedWebhooksRequestSchema,
  ListStaleWebhooksRequestSchema,
  ReplayFailedWebhooksRequestSchema,
  ReplayStaleWebhooksRequestSchema,
} from '../../gen/cortado/v1/payments_pb'
import type { StripeWebhook } from '../../gen/cortado/v1/payments_pb'
import { AdminListOrganizationsRequestSchema } from '../../gen/cortado/v1/admin_pb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '../../components/ui/sortable-header'
import {
  AlertTriangle,
  Building2,
  Clock,
  RefreshCw,
  RotateCcw,
  Zap,
  Loader2,
  CheckCircle2,
  XCircle,
  CircleDot,
  Timer,
} from 'lucide-react'
import { cn } from '../../lib/utils'

export const Route = createFileRoute('/_admin/webhooks')({
  component: AdminWebhooks,
})

type Tab = 'failed' | 'stale'
type WebhookSortField = 'eventId' | 'type' | 'status' | 'organization' | 'updated'

function getStatusBadge(status: string) {
  switch (status.toLowerCase()) {
    case 'completed':
      return { variant: 'success' as const, icon: CheckCircle2, label: 'Completed' }
    case 'failed':
      return { variant: 'danger' as const, icon: XCircle, label: 'Failed' }
    case 'pending':
      return { variant: 'warning' as const, icon: Clock, label: 'Pending' }
    case 'processing':
      return { variant: 'info' as const, icon: CircleDot, label: 'Processing' }
    default:
      return { variant: 'outline' as const, icon: CircleDot, label: status || 'Unknown' }
  }
}

function formatEventType(eventType: string) {
  return eventType.replace(/\./g, ' \u203A ')
}

function timeAgo(timestamp?: { seconds: bigint }) {
  if (!timestamp) return '-'
  const now = Date.now()
  const then = Number(timestamp.seconds) * 1000
  const diff = now - then

  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`

  return new Date(then).toLocaleDateString()
}

function AdminWebhooks() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('failed')
  const [orgFilter, setOrgFilter] = useState<string>('')
  const [selectedWebhook, setSelectedWebhook] = useState<StripeWebhook | null>(null)
  const { sortField, sortDirection, toggleSort } = useTableSort<WebhookSortField>('type')

  // Fetch orgs for the filter dropdown
  const { data: orgsData } = useQuery(
    adminListOrganizations,
    create(AdminListOrganizationsRequestSchema, {}),
  )
  const orgs = orgsData?.organizations ?? []

  // The webhook-admin RPCs require an explicit organization_id (no
  // cross-tenant "all organizations" query), so nothing fires until the
  // admin picks one.
  const hasOrgSelected = orgFilter !== ''

  // Failed webhooks query
  const failedQuery = useQuery(
    listFailedWebhooks,
    create(ListFailedWebhooksRequestSchema, {
      limit: 200,
      organizationId: orgFilter,
    }),
    { enabled: hasOrgSelected },
  )

  // Stale webhooks query
  const staleQuery = useQuery(
    listStaleWebhooks,
    create(ListStaleWebhooksRequestSchema, {
      staleMinutes: 30,
      limit: 200,
      organizationId: orgFilter,
    }),
    { enabled: hasOrgSelected },
  )

  const failedQueryKey = createConnectQueryKey({
    schema: listFailedWebhooks,
    cardinality: undefined,
  })
  const staleQueryKey = createConnectQueryKey({
    schema: listStaleWebhooks,
    cardinality: undefined,
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: failedQueryKey })
    queryClient.invalidateQueries({ queryKey: staleQueryKey })
  }

  // Replay single webhook
  const replaySingleMutation = useMutation(replayWebhook, {
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Webhook replayed', { description: data.workflowId ? `Workflow: ${data.workflowId}` : undefined })
      } else {
        toast.error('Replay failed', { description: data.message })
      }
      invalidateAll()
    },
    onError: (err: Error) => {
      toast.error(`Replay failed: ${err.message}`)
    },
  })

  // Replay all failed
  const replayAllFailedMutation = useMutation(replayFailedWebhooks, {
    onSuccess: (data) => {
      toast.success(`Replayed ${data.replayedCount} failed webhooks`, {
        description: data.failedCount > 0 ? `${data.failedCount} could not be replayed` : undefined,
      })
      invalidateAll()
    },
    onError: (err: Error) => {
      toast.error(`Bulk replay failed: ${err.message}`)
    },
  })

  // Replay all stale
  const replayAllStaleMutation = useMutation(replayStaleWebhooks, {
    onSuccess: (data) => {
      toast.success(`Replayed ${data.replayedCount} stale webhooks`, {
        description: data.failedCount > 0 ? `${data.failedCount} could not be replayed` : undefined,
      })
      invalidateAll()
    },
    onError: (err: Error) => {
      toast.error(`Bulk replay failed: ${err.message}`)
    },
  })

  const activeQuery = activeTab === 'failed' ? failedQuery : staleQuery
  const totalCount = activeQuery.data?.totalCount ?? 0
  const isLoading = activeQuery.isLoading

  const failedCount = failedQuery.data?.totalCount ?? 0
  const staleCount = staleQuery.data?.totalCount ?? 0

  // Resolve org name from ID
  const orgMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const org of orgs) {
      map.set(org.id, org.name)
    }
    return map
  }, [orgs])

  const webhooks = useMemo(() => {
    const list = activeQuery.data?.webhooks ?? []
    const dir = sortDirection === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      switch (sortField) {
        case 'eventId':
          return compareStrings(a.eventId, b.eventId) * dir
        case 'type':
          return compareStrings(a.eventType, b.eventType) * dir
        case 'status':
          return compareStrings(a.webhookStatus, b.webhookStatus) * dir
        case 'organization': {
          const aName = a.organizationId ? orgMap.get(a.organizationId) ?? a.organizationId : ''
          const bName = b.organizationId ? orgMap.get(b.organizationId) ?? b.organizationId : ''
          return compareStrings(aName, bName) * dir
        }
        case 'updated':
          return compareNumbers(
            a.updatedAt?.seconds ?? 0n,
            b.updatedAt?.seconds ?? 0n,
          ) * dir
        default:
          return 0
      }
    })
  }, [activeQuery.data?.webhooks, sortField, sortDirection, orgMap])

  const isBulkReplaying = replayAllFailedMutation.isPending || replayAllStaleMutation.isPending

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">Webhooks</h1>
          <p className="text-muted-foreground text-sm">
            Monitor and replay Stripe connected account webhooks
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
          onClick={invalidateAll}
          disabled={failedQuery.isFetching || staleQuery.isFetching}
        >
          <RefreshCw className={cn('mr-2 h-3.5 w-3.5', (failedQuery.isFetching || staleQuery.isFetching) && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Tabs + Filters */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex w-full items-center gap-1 rounded-lg border bg-muted/50 p-1 sm:w-auto">
          <button
            onClick={() => setActiveTab('failed')}
            className={cn(
              'flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              activeTab === 'failed'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <XCircle className="h-3.5 w-3.5" />
            Failed
            {failedCount > 0 && (
              <span className={cn(
                'rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                activeTab === 'failed'
                  ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
                  : 'bg-muted text-muted-foreground',
              )}>
                {failedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('stale')}
            className={cn(
              'flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              activeTab === 'stale'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Timer className="h-3.5 w-3.5" />
            Stale
            {staleCount > 0 && (
              <span className={cn(
                'rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                activeTab === 'stale'
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300'
                  : 'bg-muted text-muted-foreground',
              )}>
                {staleCount}
              </span>
            )}
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {activeTab === 'failed' && failedCount > 0 && (
            <Button
              variant="default"
              size="sm"
              className="w-full sm:w-auto"
              disabled={isBulkReplaying}
              onClick={() =>
                replayAllFailedMutation.mutate(
                  create(ReplayFailedWebhooksRequestSchema, {
                    limit: 100,
                    organizationId: orgFilter,
                  }),
                )
              }
            >
              {replayAllFailedMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Zap className="mr-2 h-3.5 w-3.5" />
              )}
              Replay All Failed
            </Button>
          )}

          {activeTab === 'stale' && staleCount > 0 && (
            <Button
              variant="default"
              size="sm"
              className="w-full sm:w-auto"
              disabled={isBulkReplaying}
              onClick={() =>
                replayAllStaleMutation.mutate(
                  create(ReplayStaleWebhooksRequestSchema, {
                    staleMinutes: 30,
                    limit: 100,
                    organizationId: orgFilter,
                  }),
                )
              }
            >
              {replayAllStaleMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Zap className="mr-2 h-3.5 w-3.5" />
              )}
              Replay All Stale
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader
                  active={sortField === 'eventId'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('eventId')}
                >
                  Event ID
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'type'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('type')}
                >
                  Type
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'status'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('status')}
                >
                  Status
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'organization'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('organization')}
                >
                  Organization
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'updated'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('updated')}
                >
                  Updated
                </SortableHeader>
              </TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!hasOrgSelected ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Building2 className="h-8 w-8 text-muted-foreground/60" />
                    <p className="font-medium">Select an organization</p>
                    <p className="text-sm">Choose an organization above to view its webhooks</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading webhooks...
                </TableCell>
              </TableRow>
            ) : webhooks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    {activeTab === 'failed' ? (
                      <>
                        <CheckCircle2 className="h-8 w-8 text-green-500/60" />
                        <p className="font-medium">No failed webhooks</p>
                        <p className="text-sm">All webhook deliveries are healthy</p>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-8 w-8 text-green-500/60" />
                        <p className="font-medium">No stale webhooks</p>
                        <p className="text-sm">All pending webhooks are processing normally</p>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              webhooks.map((wh) => {
                const status = getStatusBadge(wh.webhookStatus)
                const StatusIcon = status.icon
                return (
                  <TableRow
                    key={wh.eventId}
                    className="cursor-pointer"
                    onClick={() => setSelectedWebhook(wh)}
                  >
                    <TableCell className="font-mono text-xs">
                      {wh.eventId.length > 28
                        ? `${wh.eventId.slice(0, 14)}...${wh.eventId.slice(-10)}`
                        : wh.eventId}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatEventType(wh.eventType)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={status.variant} className="gap-1">
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {wh.organizationId
                        ? orgMap.get(wh.organizationId) || wh.organizationId.slice(0, 8) + '...'
                        : <span className="italic">none</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {timeAgo(wh.updatedAt)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        disabled={replaySingleMutation.isPending}
                        onClick={() =>
                          replaySingleMutation.mutate({
                            eventId: wh.eventId,
                            organizationId: wh.organizationId,
                          })
                        }
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />
                        Replay
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {totalCount > 0 && (
        <p className="text-xs text-muted-foreground">
          Showing {webhooks.length} of {totalCount} {activeTab} webhooks
        </p>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selectedWebhook} onOpenChange={(open) => !open && setSelectedWebhook(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Webhook Detail
            </DialogTitle>
          </DialogHeader>
          {selectedWebhook && (
            <div className="space-y-4">
              <div className="grid grid-cols-[100px_1fr] gap-y-3 text-sm">
                <span className="text-muted-foreground">Event ID</span>
                <span className="font-mono text-xs break-all">{selectedWebhook.eventId}</span>

                <span className="text-muted-foreground">Type</span>
                <span>{selectedWebhook.eventType}</span>

                <span className="text-muted-foreground">Object ID</span>
                <span className="font-mono text-xs break-all">{selectedWebhook.objectId}</span>

                <span className="text-muted-foreground">Status</span>
                <span>
                  <Badge variant={getStatusBadge(selectedWebhook.webhookStatus).variant}>
                    {getStatusBadge(selectedWebhook.webhookStatus).label}
                  </Badge>
                </span>

                <span className="text-muted-foreground">Organization</span>
                <span>
                  {selectedWebhook.organizationId
                    ? orgMap.get(selectedWebhook.organizationId) || selectedWebhook.organizationId
                    : <span className="italic text-muted-foreground">none</span>}
                </span>

                <span className="text-muted-foreground">Created</span>
                <span>
                  {selectedWebhook.createdAt
                    ? new Date(Number(selectedWebhook.createdAt.seconds) * 1000).toLocaleString()
                    : '-'}
                </span>

                <span className="text-muted-foreground">Updated</span>
                <span>
                  {selectedWebhook.updatedAt
                    ? new Date(Number(selectedWebhook.updatedAt.seconds) * 1000).toLocaleString()
                    : '-'}
                </span>
              </div>

              <div className="flex justify-end border-t pt-4">
                <Button
                  size="sm"
                  disabled={replaySingleMutation.isPending}
                  onClick={() => {
                    replaySingleMutation.mutate({
                      eventId: selectedWebhook.eventId,
                      organizationId: selectedWebhook.organizationId,
                    })
                    setSelectedWebhook(null)
                  }}
                >
                  {replaySingleMutation.isPending ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  )}
                  Replay Webhook
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
